/**
 * DrawingView — Construction drawing panel for J.A.D.E
 *
 * Features:
 *  • Floor plan view (hatched, dimensioned, scheduled)
 *  • Multiple named section cuts — stored in st.drawing.sectionCuts
 *  • Section view per cut (auto-generated from DCEL + cut line)
 *  • Section cuts sidebar — add, rename, delete, switch active
 *  • SVG export, Print/PDF
 *  • Scale, sheet, north rotation, drawing number controls
 */
import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import FloorPlanDrawing from "./drawing/FloorPlanDrawing";
import SectionDrawing   from "./drawing/SectionDrawing";
import { SCALES, SHEETS } from "../engine/drawingStandards";
import { worldToSheet, getPlanOrigin } from "./drawing/drawingCoords";
import { mmToPx } from "./drawing/DrawingUtils";
import { mutUpdateDrawing, mutAddSectionCut, mutDeleteSectionCut } from "../engine/layerMutations";

const MONO = { fontFamily: "'JetBrains Mono','Fira Code',monospace" };
const VIEWS = [
  { id:"plan",    label:"Floor Plan" },
  { id:"section", label:"Section"   },
];

// ── Tiny label ────────────────────────────────────────────────────────────────
function Lbl({ color, children }) {
  return (
    <span style={{ fontSize:8, letterSpacing:1, fontWeight:700, color,
      background:color+"18", border:`1px solid ${color}30`,
      padding:"1px 6px", borderRadius:2, ...MONO }}>
      {children}
    </span>
  );
}

// ── Section cuts sidebar ──────────────────────────────────────────────────────
function CutsSidebar({ cuts, activeCutId, onSelect, onDelete, onAdd, onRename }) {
  return (
    <div style={{ width:160, background:"#030508", borderRight:"1px solid #0d1520",
      display:"flex", flexDirection:"column", flexShrink:0 }}>
      <div style={{ padding:"6px 8px", borderBottom:"1px solid #0d1520",
        fontSize:8, color:"#2a3a4a", letterSpacing:2, ...MONO }}>
        SECTIONS
      </div>
      <div style={{ flex:1, overflowY:"auto" }}>
        {cuts.length === 0 && (
          <div style={{ padding:"10px 8px", fontSize:9, color:"#1e2a36", ...MONO }}>
            No sections yet
          </div>
        )}
        {cuts.map(cut => (
          <div key={cut.id}
            onClick={() => onSelect(cut.id)}
            style={{ padding:"5px 8px", borderBottom:"1px solid #06111e",
              background:activeCutId===cut.id?"#0c1828":"transparent",
              borderLeft:`2px solid ${activeCutId===cut.id?"#fb923c":"transparent"}`,
              cursor:"pointer", display:"flex", alignItems:"center", gap:4 }}>
            <span style={{ flex:1, fontSize:10, color:activeCutId===cut.id?"#fb923c":"#4a5e70", ...MONO }}>
              {cut.label}
            </span>
            <span style={{ fontSize:8, color:"#1e2a36", ...MONO }}>
              {cut.lookDir==="left"?"↑":"↓"}
            </span>
            <button
              onClick={e => { e.stopPropagation(); onDelete(cut.id); }}
              style={{ background:"transparent", border:"none", color:"#2a3a4a",
                cursor:"pointer", fontSize:10, padding:"0 2px", lineHeight:1 }}>
              ×
            </button>
          </div>
        ))}
      </div>
      <button onClick={onAdd}
        style={{ margin:"6px 8px", padding:"4px 0", background:"transparent",
          border:"1px solid #0d1520", color:"#3b82f6", borderRadius:2,
          cursor:"pointer", fontSize:9, ...MONO }}>
        + New section cut
      </button>
    </div>
  );
}

export default function DrawingView({ st, commit, onClose }) {
  const drawing   = st.drawing || {};
  const [view,     setView]     = useState("plan");
  const [scale,    setScale]    = useState(drawing.scale    || 100);
  const [sheetKey, setSheetKey] = useState(drawing.sheetKey || "A3");
  const [drawingNo,setDrawingNo]= useState(drawing.drawingNo|| "JADE-FP-01");

  // North rotation is single source of truth in st.drawing
  const northRotation  = drawing.northRotation || 0;
  const setNorthRotation = v => commit && commit(s => mutUpdateDrawing({ northRotation:v }, s));

  // ── Multiple section cuts ─────────────────────────────────────────────────
  const allCuts   = drawing.sectionCuts || [];
  const [activeCutId, setActiveCutId] = useState(allCuts[0]?.id || null);
  const activeCut = allCuts.find(c => c.id === activeCutId) || null;

  // Cut placement state
  const [cutMode,   setCutMode]   = useState(false);
  const [cutStart,  setCutStart]  = useState(null);
  const [cursorW,   setCursorW]   = useState(null);
  const [newLabel,  setNewLabel]  = useState("A-A");
  const [newLookDir,setNewLookDir]= useState("left");

  // Pan/zoom
  const [pan,  setPan]  = useState({ x:20, y:20 });
  const [zoom, setZoom] = useState(0.7);
  const svgRef    = useRef(null);
  const isPanning = useRef(false);
  const panRef    = useRef(null);

  const sheet = SHEETS[sheetKey];

  // Persist settings
  useEffect(() => {
    if (commit) commit(s => mutUpdateDrawing({ scale, sheetKey, drawingNo }, s));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale, sheetKey, drawingNo]);

  // ── Coordinate converter ──────────────────────────────────────────────────
  const evW = useCallback(e => {
    const r = svgRef.current?.getBoundingClientRect();
    if (!r) return { x:0, y:0 };
    const sheetX = (e.clientX - r.left - pan.x) / zoom;
    const sheetY = (e.clientY - r.top  - pan.y) / zoom;
    const { offX, offY } = getPlanOrigin(st, scale, sheetKey);
    const PX_PER_MM = 3.78;
    return {
      x: (sheetX - offX) * scale / PX_PER_MM,
      y: (sheetY - offY) * scale / PX_PER_MM,
    };
  }, [pan, zoom, st, scale, sheetKey]);

  const onMM = useCallback(e => {
    if (isPanning.current && panRef.current) {
      setPan({ x:panRef.current.px + e.clientX - panRef.current.mx,
               y:panRef.current.py + e.clientY - panRef.current.my });
      return;
    }
    if (cutMode) setCursorW(evW(e));
  }, [cutMode, evW]);

  const onMD = useCallback(e => {
    if (e.button===1 || (e.button===0 && e.altKey)) {
      isPanning.current = true;
      panRef.current = { mx:e.clientX, my:e.clientY, px:pan.x, py:pan.y };
      e.preventDefault();
    }
  }, [pan]);

  const onMU = useCallback(() => { isPanning.current=false; panRef.current=null; }, []);
  const onWh = useCallback(e => {
    e.preventDefault();
    setZoom(z => Math.min(4, Math.max(0.2, z*(e.deltaY<0?1.12:0.88))));
  }, []);

  const onCanvasClick = useCallback(e => {
    if (isPanning.current || !cutMode) return;
    const pt = evW(e);
    if (!cutStart) {
      setCutStart(pt);
    } else {
      const newCut = {
        x1:cutStart.x, y1:cutStart.y, x2:pt.x, y2:pt.y,
        label:newLabel, lookDir:newLookDir,
      };
      if (commit) commit(s => mutAddSectionCut(newCut, s));
      // After commit, update activeCutId — new cut id is generated inside mutAddSectionCut
      // We'll find the new cut in the next render via allCuts
      setCutStart(null);
      setCutMode(false);
      setView("section");
    }
  }, [cutMode, cutStart, evW, newLabel, newLookDir, commit]);

  // Sync activeCutId when a new cut is added
  useEffect(() => {
    const cuts = (st.drawing||{}).sectionCuts||[];
    if (cuts.length>0 && !cuts.find(c=>c.id===activeCutId)) {
      setActiveCutId(cuts[cuts.length-1].id);
    }
  }, [(st.drawing||{}).sectionCuts?.length]); // eslint-disable-line

  // ── Export / print ────────────────────────────────────────────────────────
  const exportSVG = () => {
    const container = document.getElementById("jade-drawing-export");
    if (!container) return;
    const svgEl = container.querySelector("svg");
    if (!svgEl) return;
    const blob = new Blob([svgEl.outerHTML], { type:"image/svg+xml" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${drawingNo}.svg`;
    a.click();
  };

  // ── Cut line overlay rendering ────────────────────────────────────────────
  const renderCutOverlays = () => {
    const persistedCuts = (st.drawing||{}).sectionCuts||[];
    return (
      <svg style={{ position:"absolute", inset:0, width:"100%", height:"100%", pointerEvents:"none" }}>
        <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}>
          {/* Live placement preview */}
          {cutMode && cutStart && cursorW && (() => {
            const A = worldToSheet(cutStart, st, scale, sheetKey);
            const B = worldToSheet(cursorW,  st, scale, sheetKey);
            return (
              <g>
                <line x1={A.x} y1={A.y} x2={B.x} y2={B.y}
                  stroke="#fb923c" strokeWidth={1.5/zoom}
                  strokeDasharray={`${mmToPx(6)/zoom} ${mmToPx(3)/zoom}`}/>
                <circle cx={A.x} cy={A.y} r={mmToPx(2.5)/zoom} fill="#fb923c"/>
                <text x={(A.x+B.x)/2} y={(A.y+B.y)/2-mmToPx(3.5)/zoom}
                  textAnchor="middle" fontSize={mmToPx(3)/zoom}
                  fill="#fb923c" fontFamily="monospace">{newLabel}</text>
              </g>
            );
          })()}

          {/* All persisted cuts */}
          {persistedCuts.map(cut => {
            const A   = worldToSheet({x:cut.x1,y:cut.y1}, st, scale, sheetKey);
            const B   = worldToSheet({x:cut.x2,y:cut.y2}, st, scale, sheetKey);
            const len = Math.sqrt((B.x-A.x)**2+(B.y-A.y)**2)||1;
            const nx  = -(B.y-A.y)/len, ny=(B.x-A.x)/len;
            const tL  = mmToPx(5)/zoom;
            const isActive = cut.id === activeCutId;
            const col = isActive ? "#fb923c" : "#4a5e70";
            return (
              <g key={cut.id} style={{ pointerEvents:"all", cursor:"pointer" }}
                onClick={() => { setActiveCutId(cut.id); setView("section"); }}>
                <line x1={A.x} y1={A.y} x2={B.x} y2={B.y}
                  stroke={col} strokeWidth={(isActive?1.5:0.8)/zoom}
                  strokeDasharray={`${mmToPx(6)/zoom} ${mmToPx(3)/zoom} ${mmToPx(1.5)/zoom} ${mmToPx(3)/zoom}`}/>
                {[A,B].map(([pt,i]) => {
                  const p = [A,B][i||0]; // fallback
                  return null;
                })}
                {/* Direction ticks at each end */}
                {[[A.x,A.y],[B.x,B.y]].map(([ex,ey],i)=>(
                  <g key={i}>
                    <line x1={ex} y1={ey} x2={ex+nx*tL} y2={ey+ny*tL}
                      stroke={col} strokeWidth={(isActive?1.2:0.6)/zoom}/>
                    <circle cx={ex+nx*tL*1.8} cy={ey+ny*tL*1.8}
                      r={tL*0.85} fill="none" stroke={col} strokeWidth={0.8/zoom}/>
                    <text x={ex+nx*tL*1.8} y={ey+ny*tL*1.8+mmToPx(1.2)/zoom}
                      textAnchor="middle" fontSize={mmToPx(2.8)/zoom}
                      fill={col} fontFamily="monospace" fontWeight={isActive?"700":"400"}>
                      {cut.label}
                    </text>
                  </g>
                ))}
              </g>
            );
          })}
        </g>
      </svg>
    );
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"#030508", zIndex:150,
      display:"flex", flexDirection:"column", ...MONO }}>

      {/* ── Toolbar ────────────────────────────────────────────────────────── */}
      <div style={{ height:38, background:"#030508", borderBottom:"1px solid #0d1520",
        display:"flex", alignItems:"center", padding:"0 10px", gap:8, flexShrink:0 }}>

        <button onClick={onClose}
          style={{ background:"transparent", border:"1px solid #0d1520", color:"#4a5e70",
            padding:"3px 10px", borderRadius:2, cursor:"pointer", fontSize:9, ...MONO }}>
          ← J.A.D.E
        </button>

        <div style={{ width:1, background:"#0d1520", height:18 }}/>

        {/* View tabs */}
        {VIEWS.map(v => (
          <button key={v.id} onClick={() => setView(v.id)}
            style={{ padding:"3px 12px", borderRadius:2, fontSize:9, cursor:"pointer",
              background:view===v.id?"#0c1828":"transparent",
              border:view===v.id?"1px solid #3b82f640":"1px solid transparent",
              color:view===v.id?"#60a5fa":"#2a3a4a", ...MONO }}>
            {v.label}
          </button>
        ))}

        <div style={{ width:1, background:"#0d1520", height:18 }}/>

        {/* Scale */}
        <span style={{ fontSize:8, color:"#2a3a4a" }}>SCALE</span>
        <select value={scale} onChange={e=>setScale(Number(e.target.value))}
          style={{ height:22, fontSize:9, padding:"0 4px" }}>
          {SCALES.map(s=><option key={s.value} value={s.value}>{s.label}</option>)}
        </select>

        {/* Sheet */}
        <span style={{ fontSize:8, color:"#2a3a4a" }}>SHEET</span>
        <select value={sheetKey} onChange={e=>setSheetKey(e.target.value)}
          style={{ height:22, fontSize:9, padding:"0 4px" }}>
          {Object.keys(SHEETS).map(k=><option key={k} value={k}>{k}</option>)}
        </select>

        {/* North */}
        <span style={{ fontSize:8, color:"#2a3a4a" }}>N°</span>
        <input type="number" value={northRotation} onChange={e=>setNorthRotation(Number(e.target.value))}
          style={{ width:38, height:22, fontSize:9, padding:"0 4px", textAlign:"center" }}/>

        {/* Drawing No */}
        <span style={{ fontSize:8, color:"#2a3a4a" }}>DWG</span>
        <input type="text" value={drawingNo} onChange={e=>setDrawingNo(e.target.value)}
          style={{ width:96, height:22, fontSize:9, padding:"0 4px" }}/>

        <div style={{ width:1, background:"#0d1520", height:18 }}/>

        {/* Section cut placement */}
        {view==="section" && (
          <>
            <span style={{ fontSize:8, color:"#2a3a4a" }}>LABEL</span>
            <input type="text" value={newLabel} onChange={e=>setNewLabel(e.target.value)}
              style={{ width:36, height:22, fontSize:9, padding:"0 4px" }}/>
            <select value={newLookDir} onChange={e=>setNewLookDir(e.target.value)}
              style={{ height:22, fontSize:9, padding:"0 4px" }}>
              <option value="left">↑ Left</option>
              <option value="right">↓ Right</option>
            </select>
            <button onClick={() => { setCutMode(c=>!c); setCutStart(null); setView("plan"); }}
              style={{ padding:"3px 10px", borderRadius:2, cursor:"pointer", fontSize:9,
                background:cutMode?"#1c0c00":"#060f1e",
                border:cutMode?"1px solid #fb923c60":"1px solid #0d1520",
                color:cutMode?"#fb923c":"#3b82f6", ...MONO }}>
              ✂ {cutMode?"Place cut…":"New cut"}
            </button>
          </>
        )}

        <div style={{ flex:1 }}/>

        <button onClick={exportSVG}
          style={{ padding:"3px 10px", background:"transparent",
            border:"1px solid #4ade8030", color:"#4ade80",
            borderRadius:2, cursor:"pointer", fontSize:9, ...MONO }}>
          ↓ SVG
        </button>
        <button onClick={() => window.print()}
          style={{ padding:"3px 10px", background:"transparent",
            border:"1px solid #3b82f630", color:"#3b82f6",
            borderRadius:2, cursor:"pointer", fontSize:9, ...MONO }}>
          🖨 Print
        </button>
      </div>

      {/* ── Status bar ─────────────────────────────────────────────────────── */}
      <div style={{ height:20, background:"#030508", borderBottom:"1px solid #06111e",
        display:"flex", alignItems:"center", padding:"0 10px", gap:12,
        fontSize:8, color:"#1e2a36", flexShrink:0, ...MONO }}>
        <Lbl color="#3b82f6">1:{scale}</Lbl>
        <Lbl color="#4ade80">{sheetKey}</Lbl>
        {activeCut && <Lbl color="#fb923c">SEC {activeCut.label}</Lbl>}
        {cutMode && <span style={{ color:"#fb923c" }}>Click two points on the plan to define cut plane</span>}
        {cutStart && !cutMode && <span style={{ color:"#fb923c" }}>Cut start placed — click second point</span>}
        <span style={{ marginLeft:"auto" }}>Alt+drag pan · scroll zoom</span>
      </div>

      {/* ── Main area ──────────────────────────────────────────────────────── */}
      <div style={{ flex:1, display:"flex", overflow:"hidden" }}>

        {/* Cuts sidebar (section view only) */}
        {view==="section" && (
          <CutsSidebar
            cuts={(st.drawing||{}).sectionCuts||[]}
            activeCutId={activeCutId}
            onSelect={id => { setActiveCutId(id); }}
            onDelete={id => {
              if (commit) commit(s => mutDeleteSectionCut(id, s));
              const remaining = ((st.drawing||{}).sectionCuts||[]).filter(c=>c.id!==id);
              setActiveCutId(remaining[0]?.id||null);
            }}
            onAdd={() => { setCutMode(true); setCutStart(null); setView("plan"); }}
            onRename={(id, label) => {
              if (commit) commit(s => ({
                ...s, drawing: { ...s.drawing,
                  sectionCuts:(s.drawing?.sectionCuts||[]).map(c=>c.id===id?{...c,label}:c) }
              }));
            }}
          />
        )}

        {/* ── Floor plan / section canvas ──────────────────────────────────── */}
        <div style={{ flex:1, position:"relative", overflow:"hidden", background:"#0a0a12" }}>
          {(cutMode || view==="plan") ? (
            /* Plan view with cut overlay */
            <div ref={svgRef}
              style={{ width:"100%", height:"100%", overflow:"hidden",
                position:"relative", cursor:cutMode?"crosshair":"default" }}
              onMouseMove={onMM} onMouseDown={onMD} onMouseUp={onMU}
              onClick={onCanvasClick} onWheel={onWh}>
              <div style={{ position:"absolute", transformOrigin:"0 0",
                transform:`translate(${pan.x}px,${pan.y}px) scale(${zoom})` }}>
                <div id="jade-drawing-export">
                  <FloorPlanDrawing
                    st={st} scale={scale}
                    sheetSize={{ w:sheet.w, h:sheet.h }}
                    northRotation={northRotation}
                    drawingNo={drawingNo}
                  />
                </div>
              </div>
              {renderCutOverlays()}
            </div>
          ) : (
            /* Section view */
            <div style={{ width:"100%", height:"100%", overflow:"auto",
              display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
              {activeCut ? (
                (() => {
                  try {
                    return (
                      <div id="jade-drawing-export"
                        style={{ boxShadow:"0 8px 40px #0008", display:"inline-block" }}>
                        <SectionDrawing
                          st={st} cut={activeCut} lookDir={activeCut.lookDir||"left"}
                          scale={scale}
                          sheetSize={{ w:sheet.w, h:sheet.h }}
                          drawingNo={drawingNo.replace("FP","SC")+"-"+activeCut.label}
                          sectionLabel={activeCut.label}
                        />
                      </div>
                    );
                  } catch(err) {
                    return (
                      <div style={{ color:"#f87171", padding:20, fontSize:10, ...MONO }}>
                        Section error: {String(err)}<br/>
                        Try re-placing the cut.
                      </div>
                    );
                  }
                })()
              ) : (
                <div style={{ textAlign:"center", color:"#2a3a4a" }}>
                  <div style={{ fontSize:32, marginBottom:12, opacity:0.3 }}>✂</div>
                  <div style={{ fontSize:10, marginBottom:6, ...MONO }}>NO SECTION CUT</div>
                  <div style={{ fontSize:9, color:"#1e2a36", ...MONO, lineHeight:1.8 }}>
                    Click "New cut" above then place two points<br/>on the floor plan to define the cut plane.
                  </div>
                  <button onClick={() => { setCutMode(true); setCutStart(null); setView("plan"); }}
                    style={{ marginTop:16, padding:"5px 16px", background:"transparent",
                      border:"1px solid #0d1520", color:"#3b82f6",
                      borderRadius:2, cursor:"pointer", fontSize:9, ...MONO }}>
                    Place cut →
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @media print {
          body > *:not(#jade-drawing-export) { display:none!important; }
          #jade-drawing-export { display:block!important; }
          @page { size:${sheetKey==="A4"?"A4":sheetKey==="A3"?"A3":sheetKey==="A2"?"A2":"A1"} landscape; margin:0; }
        }
      `}</style>
    </div>
  );
}
